import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { loadsAPI, loadRequestsAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { VerificationBadge, LoadStatusBadge, RoleBadge, RequestStatusBadge, RatingBadge } from '../components/Badges';
import { TrustSignals } from '../components/FraudShieldBadge';
import ReportIssueDialog from '../components/ReportIssueDialog';
import {
  MapPin, Calendar, DollarSign, Truck, Package, User, Phone, Mail,
  Building, ShieldCheck, ArrowLeft, Clock, CheckCircle, XCircle,
  Loader2, Send, FileText, Navigation, Flag
} from 'lucide-react';
import { toast } from 'sonner';

const LoadDetailPage = () => {
  const { loadId } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, user, canRequestLoads, canPostLoads } = useAuth();
  const [load, setLoad] = useState(null);
  const [history, setHistory] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [requestDialogOpen, setRequestDialogOpen] = useState(false);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [requestNotes, setRequestNotes] = useState('');
  const [newStatus, setNewStatus] = useState('');
  const [statusNote, setStatusNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);

  const isOwner = load?.poster_user_id === user?.id;
  const isAssignedCarrier = load?.assigned_carrier_user_id === user?.id;

  useEffect(() => {
    loadLoadDetails();
  }, [loadId]);

  const loadLoadDetails = async () => {
    try {
      const [loadRes, historyRes] = await Promise.all([
        loadsAPI.getOne(loadId),
        loadsAPI.getHistory(loadId),
      ]);
      setLoad(loadRes.data);
      setHistory(historyRes.data);

      // If owner, load requests
      if (loadRes.data.poster_user_id === user?.id) {
        try {
          const requestsRes = await loadRequestsAPI.getForLoad(loadId);
          setRequests(requestsRes.data);
        } catch (e) {
          // May fail if not authorized
        }
      }
    } catch (error) {
      toast.error('Failed to load details');
      navigate('/loadboard');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestHaul = async () => {
    setSubmitting(true);
    try {
      await loadRequestsAPI.create({
        load_id: loadId,
        notes: requestNotes,
      });
      toast.success('Haul request submitted!');
      setRequestDialogOpen(false);
      setRequestNotes('');
      loadLoadDetails();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  const handleApproveRequest = async (requestId) => {
    try {
      await loadRequestsAPI.approve(requestId);
      toast.success('Request approved!');
      loadLoadDetails();
    } catch (error) {
      toast.error('Failed to approve request');
    }
  };

  const handleDeclineRequest = async (requestId) => {
    try {
      await loadRequestsAPI.decline(requestId);
      toast.success('Request declined');
      loadLoadDetails();
    } catch (error) {
      toast.error('Failed to decline request');
    }
  };

  const handleUpdateStatus = async () => {
    if (!newStatus) return;
    setSubmitting(true);
    try {
      await loadsAPI.updateStatus(loadId, newStatus, statusNote);
      toast.success('Status updated!');
      setStatusDialogOpen(false);
      setNewStatus('');
      setStatusNote('');
      loadLoadDetails();
    } catch (error) {
      toast.error('Failed to update status');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatTime = (dateStr) => {
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const statusOptions = [
    { value: 'picked_up', label: 'Picked Up' },
    { value: 'delivered', label: 'Delivered' },
    { value: 'completed', label: 'Completed' },
    { value: 'cancelled', label: 'Cancelled' },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-background pt-20 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!load) return null;

  return (
    <div className="min-h-screen bg-background pt-20 pb-12 px-4">
      <div className="max-w-5xl mx-auto">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-6"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Load Header */}
            <Card className="border-white/5 bg-card/50 animate-fade-in" data-testid="load-header-card">
              <CardContent className="pt-6">
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <LoadStatusBadge status={load.status} />
                      <span className="text-sm text-muted-foreground uppercase">
                        {load.load_category?.replace('_', ' ')}
                      </span>
                    </div>
                    <h1 className="text-2xl font-bold font-['Barlow_Condensed'] uppercase tracking-wide">
                      {load.pickup_location} → {load.delivery_location}
                    </h1>
                  </div>
                  <div className="text-right">
                    <div className="text-3xl font-bold text-primary font-['Barlow_Condensed']">
                      ${load.price.toLocaleString()}
                    </div>
                    <span className="text-sm text-muted-foreground">Posted price</span>
                  </div>
                </div>

                {/* Route Details */}
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-sm bg-green-500/10 flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-5 h-5 text-green-400" />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground uppercase tracking-wider">Pickup</p>
                      <p className="font-medium">{load.pickup_location}</p>
                      <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                        <Calendar className="w-4 h-4" />
                        {formatDate(load.pickup_date)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-sm bg-red-500/10 flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-5 h-5 text-red-400" />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground uppercase tracking-wider">Delivery</p>
                      <p className="font-medium">{load.delivery_location}</p>
                      {load.delivery_date && (
                        <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                          <Calendar className="w-4 h-4" />
                          {formatDate(load.delivery_date)}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Vehicle Details */}
            <Card className="border-white/5 bg-card/50 animate-fade-in delay-100" data-testid="vehicle-details-card">
              <CardHeader>
                <CardTitle className="font-['Barlow_Condensed'] uppercase tracking-wide">
                  Vehicle Details
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="flex items-center gap-3">
                    <Truck className="w-5 h-5 text-muted-foreground" />
                    <div>
                      <p className="text-xs text-muted-foreground">Vehicle Type</p>
                      <p className="font-medium capitalize">{load.vehicle_type?.replace('_', ' ')}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Package className="w-5 h-5 text-muted-foreground" />
                    <div>
                      <p className="text-xs text-muted-foreground">Vehicle Count</p>
                      <p className="font-medium">{load.vehicle_count} vehicle(s)</p>
                    </div>
                  </div>
                  {load.vehicle_make_model && (
                    <div className="flex items-center gap-3">
                      <FileText className="w-5 h-5 text-muted-foreground" />
                      <div>
                        <p className="text-xs text-muted-foreground">Make/Model</p>
                        <p className="font-medium">{load.vehicle_make_model}</p>
                      </div>
                    </div>
                  )}
                  {load.vehicle_year && (
                    <div className="flex items-center gap-3">
                      <Calendar className="w-5 h-5 text-muted-foreground" />
                      <div>
                        <p className="text-xs text-muted-foreground">Year</p>
                        <p className="font-medium">{load.vehicle_year}</p>
                      </div>
                    </div>
                  )}
                </div>
                {load.vehicle_notes && (
                  <div className="mt-4 p-3 rounded-sm bg-secondary/30">
                    <p className="text-xs text-muted-foreground mb-1">Notes</p>
                    <p className="text-sm">{load.vehicle_notes}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Verified Load Chain (History) */}
            <Card className="border-white/5 bg-card/50 animate-fade-in delay-200" data-testid="load-history-card">
              <CardHeader>
                <CardTitle className="font-['Barlow_Condensed'] uppercase tracking-wide flex items-center gap-2">
                  <ShieldCheck className="w-5 h-5 text-primary" />
                  Verified Load Chain
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="relative">
                  {history.map((entry, index) => (
                    <div key={entry.id} className="flex gap-4 pb-6 last:pb-0">
                      <div className="relative">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                          index === history.length - 1 ? 'bg-primary text-primary-foreground' : 'bg-secondary'
                        }`}>
                          <CheckCircle className="w-4 h-4" />
                        </div>
                        {index < history.length - 1 && (
                          <div className="absolute top-8 left-4 w-0.5 h-full bg-border -translate-x-1/2" />
                        )}
                      </div>
                      <div className="flex-1 pt-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium capitalize">
                            {entry.new_status?.replace('_', ' ')}
                          </span>
                          {entry.old_status && (
                            <span className="text-xs text-muted-foreground">
                              from {entry.old_status.replace('_', ' ')}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {entry.user_name} • {formatTime(entry.created_at)}
                        </p>
                        {entry.note && (
                          <p className="text-sm mt-1 text-muted-foreground">{entry.note}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Requests (for owner) */}
            {isOwner && requests.length > 0 && (
              <Card className="border-white/5 bg-card/50 animate-fade-in delay-300" data-testid="requests-card">
                <CardHeader>
                  <CardTitle className="font-['Barlow_Condensed'] uppercase tracking-wide">
                    Haul Requests ({requests.length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {requests.map((request) => (
                    <div
                      key={request.id}
                      className="p-4 rounded-sm bg-secondary/30 border border-white/5"
                      data-testid={`request-${request.id}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center">
                            <User className="w-5 h-5" />
                          </div>
                          <div>
                            <p className="font-medium">
                              {request.carrier_profile?.full_name || 'Unknown Carrier'}
                            </p>
                            <div className="flex items-center gap-2 mt-1 flex-wrap">
                              {request.carrier_profile && (
                                <VerificationBadge
                                  status={request.carrier_profile.verification_status || 'pending'}
                                  size="sm"
                                />
                              )}
                              {request.carrier_rating && (
                                <RatingBadge 
                                  rating={request.carrier_rating} 
                                  count={request.carrier_rating_count}
                                  size="sm"
                                />
                              )}
                              <span className="text-xs text-muted-foreground">
                                {formatTime(request.created_at)}
                              </span>
                            </div>
                          </div>
                        </div>
                        <RequestStatusBadge status={request.status} />
                      </div>
                      {request.notes && (
                        <p className="mt-3 text-sm text-muted-foreground">{request.notes}</p>
                      )}
                      {request.status === 'pending' && (
                        <div className="flex gap-2 mt-4">
                          <Button
                            onClick={() => handleApproveRequest(request.id)}
                            className="glow-gold"
                            size="sm"
                            data-testid={`approve-request-${request.id}`}
                          >
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Approve
                          </Button>
                          <Button
                            variant="outline"
                            onClick={() => handleDeclineRequest(request.id)}
                            size="sm"
                            data-testid={`decline-request-${request.id}`}
                          >
                            <XCircle className="w-4 h-4 mr-1" />
                            Decline
                          </Button>
                        </div>
                      )}
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Actions */}
            <Card className="border-white/5 bg-card/50 animate-fade-in" data-testid="actions-card">
              <CardContent className="pt-6 space-y-3">
                {canRequestLoads && !isOwner && !isAssignedCarrier && load.status === 'posted' && (
                  <Button
                    className="w-full h-12 glow-gold uppercase tracking-wider font-semibold"
                    onClick={() => setRequestDialogOpen(true)}
                    data-testid="request-haul-btn"
                  >
                    <Send className="w-5 h-5 mr-2" />
                    Request Haul
                  </Button>
                )}
                {(isOwner || isAssignedCarrier) && !['completed', 'cancelled'].includes(load.status) && (
                  <Button
                    variant="outline"
                    className="w-full h-12"
                    onClick={() => setStatusDialogOpen(true)}
                    data-testid="update-status-btn"
                  >
                    <Clock className="w-5 h-5 mr-2" />
                    Update Status
                  </Button>
                )}
                {(isOwner || isAssignedCarrier) && ['picked_up', 'in_transit'].includes(load.status) && (
                  <Button
                    variant="outline"
                    className="w-full h-12 border-[#4ADE80] text-[#4ADE80] hover:bg-[#4ADE80]/10"
                    onClick={() => navigate(`/tracking/${load.id}`)}
                    data-testid="track-load-btn"
                  >
                    <Navigation className="w-5 h-5 mr-2" />
                    Track Load
                  </Button>
                )}
                {isAuthenticated && !isOwner && (
                  <Button
                    variant="outline"
                    className="w-full h-10 border-red-500/30 text-red-400 hover:bg-red-500/10"
                    onClick={() => setReportDialogOpen(true)}
                    data-testid="report-load-btn"
                  >
                    <Flag className="w-4 h-4 mr-2" />
                    Report Issue
                  </Button>
                )}
                {!isAuthenticated && (
                  <Link to="/login">
                    <Button className="w-full h-12 glow-gold" data-testid="login-to-request-btn">
                      Sign In to Request
                    </Button>
                  </Link>
                )}
              </CardContent>
            </Card>

            {/* Poster Info */}
            {load.poster_profile && (
              <Card className="border-white/5 bg-card/50 animate-fade-in delay-100" data-testid="poster-card">
                <CardHeader>
                  <CardTitle className="font-['Barlow_Condensed'] uppercase tracking-wide text-sm">
                    Posted By
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-full bg-secondary flex items-center justify-center">
                      <User className="w-6 h-6" />
                    </div>
                    <div>
                      <p className="font-medium">{load.poster_profile.full_name}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <RoleBadge role={load.poster_profile.role} />
                        <VerificationBadge
                          status={load.poster_profile.verification_status || 'pending'}
                          size="sm"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="space-y-2 text-sm">
                    {load.poster_profile.company_name && (
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Building className="w-4 h-4" />
                        {load.poster_profile.company_name}
                      </div>
                    )}
                    {load.poster_profile.mc_number && (
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <FileText className="w-4 h-4" />
                        MC# {load.poster_profile.mc_number}
                      </div>
                    )}
                  </div>
                  <Link to={`/profile/${load.poster_user_id}`}>
                    <Button variant="outline" className="w-full mt-4" size="sm">
                      View Profile
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            )}

            {/* Assigned Carrier */}
            {load.carrier_profile && (
              <Card className="border-primary/20 bg-primary/5 animate-fade-in delay-200" data-testid="carrier-card">
                <CardHeader>
                  <CardTitle className="font-['Barlow_Condensed'] uppercase tracking-wide text-sm flex items-center gap-2">
                    <Truck className="w-4 h-4 text-primary" />
                    Assigned Carrier
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
                      <Truck className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium">{load.carrier_profile.full_name}</p>
                      <VerificationBadge
                        status={load.carrier_profile.verification_status || 'pending'}
                        size="sm"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>

        {/* Request Dialog */}
        <Dialog open={requestDialogOpen} onOpenChange={setRequestDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-['Barlow_Condensed'] uppercase">Request Haul</DialogTitle>
              <DialogDescription>
                Submit your request to haul this load. The poster will review and approve.
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <label className="text-sm text-muted-foreground mb-2 block">
                Message (optional)
              </label>
              <Textarea
                placeholder="Add a message to the poster..."
                value={requestNotes}
                onChange={(e) => setRequestNotes(e.target.value)}
                rows={4}
                data-testid="request-notes-input"
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setRequestDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleRequestHaul}
                disabled={submitting}
                className="glow-gold"
                data-testid="submit-request-btn"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  'Submit Request'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Status Update Dialog */}
        <Dialog open={statusDialogOpen} onOpenChange={setStatusDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-['Barlow_Condensed'] uppercase">Update Load Status</DialogTitle>
              <DialogDescription>
                Update the current status of this load.
              </DialogDescription>
            </DialogHeader>
            <div className="py-4 space-y-4">
              <div>
                <label className="text-sm text-muted-foreground mb-2 block">New Status</label>
                <Select value={newStatus} onValueChange={setNewStatus}>
                  <SelectTrigger data-testid="status-select">
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    {statusOptions.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm text-muted-foreground mb-2 block">Note (optional)</label>
                <Textarea
                  placeholder="Add a note about this status change..."
                  value={statusNote}
                  onChange={(e) => setStatusNote(e.target.value)}
                  rows={3}
                  data-testid="status-note-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setStatusDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleUpdateStatus}
                disabled={submitting || !newStatus}
                className="glow-gold"
                data-testid="confirm-status-btn"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Updating...
                  </>
                ) : (
                  'Update Status'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Report Issue Dialog */}
        <ReportIssueDialog
          open={reportDialogOpen}
          onOpenChange={setReportDialogOpen}
          relatedLoadId={load?.id}
          relatedUserId={load?.poster_user_id}
          relatedUserName={load?.poster_profile?.full_name}
          contextType="load"
        />
      </div>
    </div>
  );
};

export default LoadDetailPage;
